import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Sample, ProjectSample, CalculationStrategies } from '../@types/samples/types';
import { Well } from '../@types/wells/types';
import { Project } from '../@types/projects/types';

// TODO: Find a better place to this enum

export enum DashboardOptions {
  SAMPLES = 'samples',
  ZONES = 'zones',
  CORRELATION = 'correlation',
  RESULTS = 'results',
  SPECIAL_CORE = 'special-core',
  GHE_INPUTS = 'ghe-inputs',
  GHE_RESULTS = 'ghe-results',
  GHE_ANALOGUE_TABLES = 'ghe-analogue-tables',
  GHE_SELECTION_EXPORT = 'ghe-selection-export',
  GHE_IX_TABLES = 'ghe-ix-tables'
}

export enum KrTypes {
  WATER_OIL = 'water-oil',
  GAS_OIL = 'gas-oil'
}

type ConfigStates = {
  showProject: boolean;
  showImportProject: boolean;
  showWell: boolean;
  showCurve: boolean;
  showForm: boolean;
  showDash: boolean;
  showResults: boolean;
  showSpecialCore: boolean;
  showGheInputs: boolean;
  showGheResults: boolean;
  showKrTable: boolean;
  showRefinementTable: boolean;
  fetchProjects: boolean;
  fetchWells: boolean;
  loadingWells: boolean;
  fetchSamples: boolean;
  loadingSamples: boolean;
  loadingMembers: boolean;
  loadingKrAnalysis: boolean;
  fetchMembers: boolean;
  changeProject: boolean;
  showAnalyze: boolean;
  openNav: boolean;
  openProjectSettings: boolean;
  openParameters: boolean;
  openEditSample: boolean;
  openEditFlowUnits: boolean;
  loadingChartSamples: boolean;
  loadingProjectSamples: boolean;
  openMembers: boolean;
  dashOption: DashboardOptions;
  editCorrelationSamples: boolean;
  krType: KrTypes;
  navWidth: number;
  refreshCounter: number;
};

type Data = {
  projects: Project[];
  wells: Well[];
  projectSamples: ProjectSample[];
  wellSamples: Sample[]
};

type DataUpdate = Partial<Data> | ((previous: Data) => Partial<Data>);

interface AppContextProps {
  configStates: ConfigStates;
  data: Data;
  updateConfigStates: (newStates: Partial<ConfigStates>) => void;
  updateData: (newData: DataUpdate) => void;
  upsertProject: (project: Project) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [configStates, setConfigStates] = useState<ConfigStates>({
    showProject: false,
    showImportProject: false,
    showWell: false,
    showCurve: false,
    showForm: false,
    showDash: false,
    showResults: false,
    showSpecialCore: false,
    showGheInputs: false,
    showGheResults: false,
    showKrTable: false,
    showRefinementTable: false,
    fetchProjects: true,
    fetchWells: false,
    loadingWells: false,
    fetchSamples: false,
    loadingSamples: false,
    loadingMembers: false,
    loadingKrAnalysis: false,
    fetchMembers: false,
    changeProject: false,
    showAnalyze: false,
    openNav: true,
    openProjectSettings: false,
    openParameters: false,
    openEditSample: false,
    openEditFlowUnits: false,
    loadingProjectSamples: false,
    loadingChartSamples: false,
    openMembers: false,
    editCorrelationSamples: false,
    dashOption: DashboardOptions.SAMPLES,
    krType: KrTypes.WATER_OIL,
    navWidth: 320,
    refreshCounter: 0
  });

  const [data, setData] = useState<Data>({
    projectSamples: [],
    projects: [],
    wells: [],
    wellSamples: []
  });

  const updateConfigStates = useCallback((newStates: Partial<ConfigStates>) => {
    setConfigStates(oldStates => ({
      ...oldStates,
      ...newStates
    }));
  }, []);

  const updateData = useCallback((newData: DataUpdate) => {
    setData(oldData => {
      const patch =
        typeof newData === 'function' ? newData(oldData) : newData;
      return { ...oldData, ...patch };
    });
  }, []);

  const upsertProject = useCallback((project: Project) => {
    setData(oldData => {
      const existingIndex = oldData.projects.findIndex(
        existingProject => existingProject.uid === project.uid
      );
      if (existingIndex === -1) {
        return { ...oldData, projects: [project, ...oldData.projects] };
      }

      const nextProjects = [...oldData.projects];
      nextProjects[existingIndex] = project;
      return { ...oldData, projects: nextProjects };
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        configStates,
        data,
        updateConfigStates,
        updateData,
        upsertProject
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }

  return context;
};
